import { useContext, useState, useEffect, useRef } from 'react'
import { useFormikContext } from 'formik'
import { useCustomEventListener, emitCustomEvent } from 'react-custom-events'
import classnames from 'classnames/bind'
import { StepperContext } from '~/components/ui/ViewStepper'
import { StoreContext } from '~/store'
import { FormContent, BookingSummary, ActionButtonList, RouteIcon, ReturnTripIcon, CalendarIcon, MailIcon, PencilIcon, ReceiptIcon, Notifications, SendConfirmation } from '~/components'
import BookingDoneHeader from './BookingDoneHeader'
import { deleteBooking, getStatus } from '~/lib/api'
import { allowCancel, allowEdit } from '~/utils/data'
import { showErrorModal } from '~/utils'
import { showModal, showContentModal } from '~/lib/modal'
import { isReturnTripEnabled, canStartReturnTrip, resolveReturnTripSuggestion, startReturnTripBooking, dismissReturnTripOffer } from '~/lib/returnTrip'
import ReturnTripOffer from '../ReturnTripOffer'
import * as common from '~/config/common.module.css'
import * as styles from './index.module.css'
import { useTranslation, Trans } from 'react-i18next'
import { track } from '../../../utils/gtm'

const cx = classnames.bind(common)

const BookingDone = ({fullscreen, currentView}) => {
  const { t } = useTranslation()
  const { values, initialValues, resetForm } = useFormikContext()
  const { reset: resetStepper } = useContext(StepperContext)
  const { config, bookings, getActiveBookings, refreshUser } = useContext(StoreContext)
  const [isLoading, setIsLoading] = useState(false)
  const [lastKnownBooking, setLastKnownBooking] = useState(null)
  const lastKnownBookingRef = useRef(null)
  const drawerRef = useRef(null)
  const [drawerHeight, setDrawerHeight] = useState(0)

  useEffect(() => {
    const el = drawerRef.current
    if (!el) return
    const observer = new ResizeObserver(() => setDrawerHeight(el.offsetHeight))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const bookingsRef = useRef(bookings)
  bookingsRef.current = bookings
  // Set on every exit that is not the user closing the details (edit, cancel, return
  // trip), so the return trip offer is only evaluated when the details are closed.
  const skipReturnTripOfferRef = useRef(false)

  const booking = bookings?.find(item => item.id === values.bookingId)
  const baseEffective = booking || lastKnownBooking || values.booking

  // bookingType can be missing from the store's active-bookings response.
  // Resolve it with three fallbacks in priority order:
  //   1. whatever the store/lastKnown has (already authoritative when present)
  //   2. values.booking from the full /v1/bookings/:id fetch after edit confirm
  //   3. values.pickupTime — a non-empty pickupTime always means PRE_BOOKING
  const resolvedBookingType = baseEffective?.bookingType
    || values.booking?.bookingType
    || (values.pickupTime ? 'PRE_BOOKING' : undefined)
  const effectiveBooking = baseEffective
    ? { ...baseEffective, bookingType: resolvedBookingType }
    : null

  // Keep lastKnownBooking in sync, preserving the resolved bookingType
  useEffect(() => {
    if (booking) {
      const resolvedType = booking.bookingType
        || values.booking?.bookingType
        || (values.pickupTime ? 'PRE_BOOKING' : undefined)
      const toSave = resolvedType !== booking.bookingType
        ? { ...booking, bookingType: resolvedType }
        : booking
      lastKnownBookingRef.current = toSave
      setLastKnownBooking(toSave)
    }
  }, [booking])

  // Return trip offer (docs/RETURNTRIP.md): the web's "booking details" is this view, and it
  // is closed by the module's reset button, which resets the stepper and unmounts us. So the
  // offer is evaluated on unmount, for the booking the user just placed, unless the exit was
  // an edit/cancel/return trip (skipReturnTripOfferRef). The resolver consumes the
  // just-placed marker, so it fires at most once per booking.
  useEffect(() => {
    if (!isReturnTripEnabled()) return
    return () => {
      if (skipReturnTripOfferRef.current) return
      const closedBooking = lastKnownBookingRef.current
      if (!closedBooking) return
      resolveReturnTripSuggestion(closedBooking, bookingsRef.current).then((suggestion) => {
        if (!suggestion) return
        showContentModal((close) => (
          <ReturnTripOffer
            booking={suggestion}
            onContinue={() => { close(); startReturnTripBooking(suggestion) }}
            onNotNow={() => { dismissReturnTripOffer(suggestion.id); close() }}
          />
        ))
      })
    }
  }, [])

  // Delayed user refresh to pick up updated voucher list after backend processes the booking
  useEffect(() => {
    if (!values.bookingId) return
    const id = setTimeout(refreshUser, 3000)
    return () => clearTimeout(id)
  }, [values.bookingId])

  // Intensive status polling — keeps the header accurate after booking drops from active list
  useEffect(() => {
    if (!values.bookingId) return
    const TERMINAL = [510, 220, 610, 620]
    const poll = async () => {
      const current = lastKnownBookingRef.current
      if (!current) return
      if (TERMINAL.includes(Number(current?.bookingStatus?.statusCode))) return
      try {
        const results = await getStatus([current])
        const statusData = results?.find(item => item.bookingId === current.id)
        if (statusData?.bookingStatus) {
          const updated = { ...current, bookingStatus: statusData.bookingStatus }
          lastKnownBookingRef.current = updated
          setLastKnownBooking(updated)
        }
      } catch (e) {
        // silently ignore — main store polling continues independently
      }
    }
    const interval = setInterval(poll, 5000)
    return () => clearInterval(interval)
  }, [values.bookingId])

  const cancelBooking = () => {
    setIsLoading(true)
    skipReturnTripOfferRef.current = true
    deleteBooking(values?.bookingId).then((response) => {
      console.log(response)
      track('cancelBooking', {bookingNumber:values?.bookingId});
      getActiveBookings()
      close()
    })
    .catch((error) => {
      if (error?.response?.error === 'ALREADY_CANCELLED_BOOKING') {
        getActiveBookings()
        close()
      }
      else if (!(error?.isUnauthorized || (error?.status === 401) || (error?.response?.status === 401 && error?.response?.error === 'Unauthorized'))) {
        console.error(error)
        showErrorModal(error?.response?.message || error?.message || 'Ett fel uppstod')
      }
    })
    .finally(() => {
      setIsLoading(false)
    })
  }

  const cancel = () => {
    showModal(COPY.booking.receipt_confirm_cancel, [
      { title: COPY.booking.button_ok, callback: cancelBooking },
      { title: COPY.booking.button_cancel },
    ])
  }

  const close = () => {
    const resetValues = initialValues;
    resetValues.isCompanyTrip = values.isCompanyTrip; // Preserve company trip status
    resetValues.company = values.company; // Preserve company name if set
    resetForm({
      values: resetValues
    })
    resetStepper()
  }

  const openSendConfirmation = () => {
    const bookingId = values.bookingId
    showContentModal((close) => (
      <SendConfirmation bookingId={bookingId} onComplete={close} />
    ))
  }

  const editBooking = () => {
    if (booking) {
      skipReturnTripOfferRef.current = true
      emitCustomEvent('EDIT_BOOKING', booking)
    }
  }

  // "Book return trip": opens the prefilled return trip sheet for this booking. Closes the
  // details first (same as the reset button) so the sheet starts from a clean form.
  const bookReturnTrip = () => {
    const original = effectiveBooking
    if (!original) return
    skipReturnTripOfferRef.current = true
    close()
    startReturnTripBooking(original)
  }
  const canBookReturnTrip = canStartReturnTrip(effectiveBooking)

  const canCancel = allowCancel(booking)
  const canEdit = allowEdit(booking)

  const actionButtons = [
    // { label: t('button-book-again'), icon: <RouteIcon />, onClick: close },
    ...(canBookReturnTrip ? [{ label: t('button-return-trip'), icon: <ReturnTripIcon />, onClick: bookReturnTrip }] : []),
    //{ label: t('button-add-to-calendar'), icon: <CalendarIcon />, onClick: () => {}, disabled: true },
    { label: t('button-forward-confirmation'), icon: <MailIcon />, onClick: openSendConfirmation },
    { label: t('button-modify-booking'), icon: <PencilIcon />, onClick: editBooking, disabled: !canEdit },
    // { label: t('button-receipt-unavailable'), icon: <ReceiptIcon />, onClick: () => {}, disabled: true },
    ...(canCancel ? [{ label: t('button-cancel-booking'), onClick: cancel, destructive: true, disabled: isLoading }] : []),
  ]

  // A return trip started from the bookings drawer replaces this view; don't offer on top of it.
  useCustomEventListener('RETURN_TRIP_BOOKING', () => { skipReturnTripOfferRef.current = true }, [])

  useCustomEventListener(
    'CANCEL_BOOKING',
    (bookingId) => {
      if (values.bookingId === bookingId) {
        skipReturnTripOfferRef.current = true
        close()
      }
    },
    [values.bookingId]
  )

  return (
    <div className={cx(common.widgetWrapper, styles.hideOverflow, { fullHeight:fullscreen, narrow:true, isLoading })} style={{ paddingBottom: 0 }}>
      <FormContent noRadius>
        <div className="-mx-6 -mt-6 mb-4">
          <BookingDoneHeader booking={effectiveBooking} />
        </div>
        <BookingSummary values={values} showHow showPayment currentView={currentView} afterLocations={<Notifications fullscreen={fullscreen}/>}/>
        <div className={`${styles.info} mt-4`}>
          <p>{t('header-thanks-for-your-booking')}</p>
          <p>
            <Trans
              i18nKey="info-booking-done"
              components={{
                link1: <a href={config.termsLink} target="_blank"/>
              }}
            />
          </p>
        </div>
        <div className="hidden app-lg:block shrink-0" style={{ height: drawerHeight }} />
      </FormContent>
      <div ref={drawerRef} className={styles.actionDrawer}>
        <ActionButtonList buttons={actionButtons} />
      </div>
    </div>
  )
}

BookingDone.validationSchema = null

export default BookingDone
